import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  AGE_GROUPS,
  chatPolicy,
  handleOf,
  isGranted,
  missingConsents,
  type AgeGroup,
  type ConsentPurpose,
} from '@il-colpo/shared/account';
import { AvatarFigure } from '../../account/AvatarFigure.tsx';
import { getAccountStore, useAccount } from '../../account/accountStore.ts';
import type { AccountUser } from '../../account/types.ts';
import { Badge, Button, Icon, Modal, Panel, SegmentedControl, Switch } from '../../ui/index.ts';
import styles from './Account.module.css';
import { FormError, useAction, useAuthMethods, useDateFormat } from './common.tsx';

const AGE_LABEL: Record<AgeGroup, 'age.under14' | 'age.from14' | 'age.adult'> = {
  under_14: 'age.under14',
  '14_to_17': 'age.from14',
  '18_plus': 'age.adult',
};

function methodsOf(user: AccountUser, labelEmail: string): string {
  const names = user.providers.map((p) =>
    p === 'email' ? labelEmail : p.charAt(0).toUpperCase() + p.slice(1),
  );
  return names.join(', ');
}

function Identity() {
  const { t } = useTranslation('account');
  const profile = useAccount((s) => s.profile);
  const user = useAccount((s) => s.snapshot.user);
  if (!user) return null;
  const handle = profile ? handleOf(profile.nickname, profile.tag) : '';
  return (
    <Panel>
      <div className={styles['identity']}>
        {profile ? (
          <AvatarFigure
            avatar={profile.avatar}
            size="5.5rem"
            label={t('profile.avatarLabel', { name: profile.nickname })}
          />
        ) : null}
        <div className={styles['identityText']}>
          <h2 className={styles['handle']}>{handle}</h2>
          <div className={styles['badges']}>
            {user.anonymous ? <Badge tone="amber">{t('overview.guest')}</Badge> : null}
            {user.email ? <span className={styles['note']}>{user.email}</span> : null}
          </div>
          {!user.anonymous && user.providers.length > 0 ? (
            <span className={styles['note']}>
              {t('overview.signedInWith', {
                methods: methodsOf(user, t('overview.methodEmail')),
              })}
            </span>
          ) : null}
        </div>
      </div>
    </Panel>
  );
}

function GuestBanner() {
  const { t } = useTranslation('account');
  const navigate = useNavigate();
  const user = useAccount((s) => s.snapshot.user);
  const methods = useAuthMethods();
  if (!user?.anonymous) return null;
  // With no e-mail and no provider to turn the guest into, there is nothing to offer.
  const upgrade = methods.code || methods.providers.length > 0;
  return (
    <Panel tone="lamp">
      <div className={styles['banner']}>
        <strong>{t('overview.guestBannerTitle')}</strong>
        <p className={styles['note']}>
          {upgrade ? t('overview.guestBannerBody') : t('overview.guestBannerBodyOnly')}
        </p>
        {upgrade ? (
          <div>
            <Button
              onClick={() => {
                void navigate('/account/security');
              }}
            >
              {t('overview.guestBannerAction')}
            </Button>
          </div>
        ) : null}
      </div>
    </Panel>
  );
}

function ConsentGate() {
  const { t } = useTranslation('account');
  const { t: tl } = useTranslation('legal');
  const consents = useAccount((s) => s.consents);
  const [read, setRead] = useState<Record<ConsentPurpose, boolean>>({
    terms: false,
    privacy: false,
    analytics: false,
  });
  const { pending, error, run } = useAction();
  if (!consents) return null;
  const missing = missingConsents(consents);
  if (missing.length === 0) return null;

  const accept = async () => {
    for (const purpose of missing) {
      const r = await run(() => getAccountStore().getState().answerConsent(purpose, true));
      if (!r.ok) return;
    }
  };

  return (
    <Panel title={t('consent.title')}>
      <div className={styles['stack']}>
        <p className={styles['note']}>{t('consent.body')}</p>
        {missing.map((purpose) => (
          <div key={purpose} className={styles['row']}>
            <Switch
              checked={read[purpose]}
              onChange={(checked) => {
                setRead((r) => ({ ...r, [purpose]: checked }));
              }}
              label={t(purpose === 'terms' ? 'consent.terms' : 'consent.privacy')}
            />
            <Link className={styles['link']} to={`/legal/${purpose}`}>
              {tl(purpose === 'terms' ? 'links.terms' : 'links.privacy')}
            </Link>
          </div>
        ))}
        <p className={styles['note']}>{t('consent.draft')}</p>
        <FormError code={error} />
        <Button
          loading={pending}
          disabled={missing.some((p) => !read[p])}
          onClick={() => {
            void accept();
          }}
        >
          {t('consent.accept')}
        </Button>
      </div>
    </Panel>
  );
}

function AgePanel() {
  const { t } = useTranslation('account');
  const profile = useAccount((s) => s.profile);
  const [group, setGroup] = useState<AgeGroup>('18_plus');
  const { pending, error, run } = useAction();
  if (!profile || profile.ageGroup !== null) return null;
  return (
    <Panel title={t('age.title')}>
      <div className={styles['stack']}>
        <p className={styles['note']}>{t('age.body')}</p>
        <SegmentedControl
          label={t('age.title')}
          hideLabel
          value={group}
          onChange={setGroup}
          options={AGE_GROUPS.map((g) => ({ value: g, label: t(AGE_LABEL[g]) }))}
        />
        <p className={styles['note']}>
          {chatPolicy(group).freeChat ? t('age.chat.free') : t('age.chat.phrases')}
        </p>
        <FormError code={error} />
        <Button
          loading={pending}
          onClick={() => {
            void run(() => getAccountStore().getState().declareAge(group));
          }}
        >
          {t('age.confirm')}
        </Button>
      </div>
    </Panel>
  );
}

function SyncPanel() {
  const { t } = useTranslation('account');
  const sync = useAccount((s) => s.sync);
  const user = useAccount((s) => s.snapshot.user);
  const { time } = useDateFormat();
  if (!user) return null;

  let line: string;
  switch (sync.state) {
    case 'off':
      line = t('sync.off');
      break;
    case 'needsConsent':
      line = t('sync.needsConsent');
      break;
    case 'syncing':
      line = t('sync.syncing');
      break;
    case 'ok':
      line = sync.at ? t('sync.ok', { time: time(sync.at) }) : t('sync.okNever');
      break;
    case 'error':
      line = t('sync.error');
      break;
    default:
      line = t('sync.idle');
  }
  const canSync = sync.state !== 'off' && sync.state !== 'needsConsent';
  return (
    <Panel title={t('sync.title')}>
      <div className={styles['stack']}>
        <div className={styles['syncLine']}>
          <span role="status">{line}</span>
          {canSync ? (
            <Button
              variant="secondary"
              loading={sync.state === 'syncing'}
              onClick={() => {
                void getAccountStore().getState().syncNow();
              }}
            >
              {t('sync.now')}
            </Button>
          ) : null}
        </div>
        <p className={styles['note']}>{t('sync.what')}</p>
      </div>
    </Panel>
  );
}

function DeletionPending() {
  const { t } = useTranslation('account');
  const deletion = useAccount((s) => s.deletion);
  const { date } = useDateFormat();
  const { pending, error, run } = useAction();
  if (!deletion) return null;
  return (
    <Panel tone="lamp">
      <div className={styles['stack']}>
        <strong role="status">
          {t('deletion.pending', { date: date(deletion.scheduledFor) })}
        </strong>
        <FormError code={error} />
        <div>
          <Button
            loading={pending}
            onClick={() => {
              void run(() => getAccountStore().getState().cancelDeletion());
            }}
          >
            {t('deletion.cancel')}
          </Button>
        </div>
      </div>
    </Panel>
  );
}

function SignOut() {
  const { t } = useTranslation('account');
  const [open, setOpen] = useState(false);
  const [wipe, setWipe] = useState(false);
  const { pending, error, run } = useAction();

  const out = async (everywhere: boolean) => {
    const r = await run(() =>
      getAccountStore().getState().signOut({ everywhere, wipeDevice: wipe }),
    );
    if (r.ok) setOpen(false);
  };

  return (
    <>
      <Button
        variant="ghost"
        onClick={() => {
          setOpen(true);
        }}
      >
        {t('overview.signOut')}
      </Button>
      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        title={t('overview.signOutTitle')}
        actions={
          <>
            <Button
              loading={pending}
              onClick={() => {
                void out(false);
              }}
            >
              {t('overview.signOutHere')}
            </Button>
            <Button
              variant="secondary"
              loading={pending}
              onClick={() => {
                void out(true);
              }}
            >
              {t('overview.signOutEverywhere')}
            </Button>
          </>
        }
      >
        <div className={styles['stack']}>
          <p className={styles['note']}>{t('overview.signOutBody')}</p>
          <Switch
            checked={wipe}
            onChange={setWipe}
            label={t('overview.signOutWipe')}
            hint={t('overview.signOutWipeHint')}
          />
          <FormError code={error} />
        </div>
      </Modal>
    </>
  );
}

export function Overview() {
  const { t } = useTranslation('account');
  const profile = useAccount((s) => s.profile);
  const consents = useAccount((s) => s.consents);
  const staff = useAccount((s) => s.snapshot.user?.staffRole ?? null);
  const ready = consents !== null && isGranted(consents, 'terms') && isGranted(consents, 'privacy');
  const links: { to: string; label: string; hint: string }[] = [
    {
      to: '/account/profile',
      label: t('overview.links.profile'),
      hint: t('overview.links.profileHint'),
    },
    {
      to: '/progress',
      label: t('overview.links.progress'),
      hint: t('overview.links.progressHint'),
    },
    {
      to: '/account/security',
      label: t('overview.links.security'),
      hint: t('overview.links.securityHint'),
    },
    {
      to: '/account/privacy',
      label: t('overview.links.privacy'),
      hint: t('overview.links.privacyHint'),
    },
    // The team's area is offered to the team only (the service checks the role itself).
    ...(staff
      ? [
          {
            to: '/admin',
            label: t('overview.links.admin'),
            hint: t('overview.links.adminHint'),
          },
        ]
      : []),
  ];

  return (
    <>
      <Identity />
      <DeletionPending />
      <GuestBanner />
      <ConsentGate />
      {ready || consents === null ? <AgePanel /> : null}
      <SyncPanel />
      {profile ? (
        <nav aria-label={t('title')}>
          <ul className={styles['navList']}>
            {links.map((l) => (
              <li key={l.to}>
                <Link className={styles['navLink']} to={l.to}>
                  <span className={styles['navText']}>
                    <strong>{l.label}</strong>
                    <span className={styles['navHint']}>{l.hint}</span>
                  </span>
                  <Icon name="arrowRight" />
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      ) : null}
      <div className={styles['rowEnd']}>
        <SignOut />
      </div>
    </>
  );
}
